import React from "react"
import {useTranslation} from "react-i18next"

// Statements
import {LEAD_NAMES, LeadLine} from "../ecgStatements"

// Styles
import useStyles from "./styles"

type LeadChipsProps = {
    line: LeadLine
    onToggle(lead: string): void
    onDone(): void
}

/**
 * The twelve leads, offered when the caret sits on a statement that wants them.
 *
 * Seven of the 48 statements end in "… in %s" and are the only ones that leave
 * typing to do after they are inserted. A chip row turns that typing into taps,
 * and — because the chips write the names — removes the two mistakes hand
 * typing makes: "avf" instead of "aVF", and leads out of report order.
 *
 * When the line holds something the chips did not write, a range like "V2-V4"
 * or a note, `line.recognised` is false: the chips then show nothing as
 * selected and adding one appends rather than rewrites, so a person's own
 * wording is never silently reformatted.
 *
 * @param props
 */
export default function LeadChips(props: LeadChipsProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const {line, onToggle, onDone} = props;

    return (
        <div className={styles.bar} role="group" aria-label={t("statements.leads_label")}>
            <span className={styles.label}>{t("statements.leads_label")}</span>

            <div className={styles.chips}>
                {LEAD_NAMES.map(lead => {
                    const active = line.recognised && line.leads.indexOf(lead) !== -1;

                    return (
                        <button
                            key={lead}
                            type="button"
                            className={`${styles.chip} ${active ? styles.chip__active : ""}`}
                            aria-pressed={active}
                            /*
                             * preventDefault on mousedown keeps focus in the
                             * textarea when the chip is clicked; the action
                             * itself hangs off click, because a keyboard
                             * activation (Tab to the chip, then Enter or
                             * Space) fires click and never mousedown. Acting
                             * on mousedown alone made the chips mouse-only.
                             */
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => onToggle(lead)}
                        >
                            {lead}
                        </button>
                    )
                })}
            </div>

            <button
                type="button"
                className={styles.done}
                onMouseDown={event => event.preventDefault()}
                onClick={onDone}
            >
                {t("statements.leads_done")}
            </button>
        </div>
    )
}
